import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { User, Building2, Mail, Phone, Tag, Loader2, CheckCircle2, Mic, BarChart3, Clock3, NotebookPen } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { API } from "../lib/site";

const FUNCTIONS = ["Appointment booking", "Answering FAQs", "Outbound reminders", "Order taking", "Emergency dispatch", "Lead qualification"];
const VOLUMES = ["Under 20 calls / week", "20–50 calls / week", "50–150 calls / week", "150+ calls / week"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 pl-11 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-violet-400/60 focus:bg-violet-400/5 focus:shadow-[0_0_20px_rgba(112,0,255,0.15)] transition-[border-color,background-color,box-shadow] duration-300";

const Field = ({ icon: Icon, label, optional, children, testId }) => (
  <div data-testid={testId}>
    <label className="mb-2 flex items-center gap-2 text-xs font-mono font-semibold tracking-widest uppercase text-slate-400">
      {label}
      {optional && <span className="text-slate-600 normal-case tracking-normal">(optional)</span>}
    </label>
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none z-10" />
      {children}
    </div>
  </div>
);

export default function VoiceInquiryForm() {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    business_type: "",
    call_volume: VOLUMES[0],
    email: "",
    phone: "",
    preferred_call_time: "",
    notes: "",
  });
  const [functions, setFunctions] = useState(["Appointment booking"]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleFunction = (fn) =>
    setFunctions((list) => (list.includes(fn) ? list.filter((x) => x !== fn) : [...list, fn]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads/voice-agent`, {
        ...form,
        voice_functions: functions,
        preferred_call_time: form.preferred_call_time || null,
        notes: form.notes || null,
      });
      setSubmitted(res.data);
      toast.success("Voice agent inquiry received — our team will call you to design your agent.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="voice-inquiry" data-testid="voice-inquiry-form-section" className="relative py-24 md:py-36 px-4 sm:px-6 lg:px-8 bg-[#0A0D14]/50 overflow-hidden">
      <div className="absolute -bottom-24 -left-24 h-[460px] w-[460px] rounded-full bg-violet-600/10 blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <Reveal delay={0.15} className="order-2 lg:order-1">
          <div className="glow-card rounded-2xl p-7 sm:p-10 border-violet-400/15">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-10"
                  data-testid="voice-inquiry-success"
                >
                  <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-violet-500/10 ring-1 ring-violet-400/40">
                    <Mic className="h-10 w-10 text-violet-300" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-slate-50">
                    Your agent is being designed
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                    We'll call <span className="text-violet-300">{form.phone}</span> to configure{" "}
                    {form.business_name || "your business"}'s voice agent and give you a live test number.
                  </p>
                  <p className="mt-5 font-mono text-xs text-slate-500">
                    REF: <span className="text-violet-300">{submitted.id.slice(0, 8).toUpperCase()}</span>
                  </p>
                  <button
                    data-testid="voice-inquiry-submit-another"
                    onClick={() => {
                      setSubmitted(null);
                      setForm({ name: "", business_name: "", business_type: "", call_volume: VOLUMES[0], email: "", phone: "", preferred_call_time: "", notes: "" });
                    }}
                    className="mt-8 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-300 hover:border-violet-400/50 hover:text-violet-300 transition-colors"
                  >
                    Submit another inquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="space-y-5"
                  data-testid="voice-inquiry-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field icon={User} label="Your name" testId="voice-field-name">
                      <input data-testid="voice-inquiry-input-name" type="text" required value={form.name} onChange={set("name")} placeholder="Jordan Lee" className={inputCls} />
                    </Field>
                    <Field icon={Building2} label="Business name" testId="voice-field-business">
                      <input data-testid="voice-inquiry-input-business" type="text" required value={form.business_name} onChange={set("business_name")} placeholder="Lee's Plumbing Co." className={inputCls} />
                    </Field>
                    <Field icon={Mail} label="Email address" testId="voice-field-email">
                      <input data-testid="voice-inquiry-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="jordan@leesplumbing.com" className={inputCls} />
                    </Field>
                    <Field icon={Phone} label="Phone number" testId="voice-field-phone">
                      <input data-testid="voice-inquiry-input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+1 (555) 010-8899" className={inputCls} />
                    </Field>
                    <Field icon={Tag} label="Industry / business type" testId="voice-field-type">
                      <select data-testid="voice-inquiry-input-type" required value={form.business_type} onChange={set("business_type")} className={`${inputCls} appearance-none cursor-pointer`}>
                        <option value="" disabled>Select your industry…</option>
                        {["Dental / Medical", "Restaurant / Café", "Salon / Spa", "Plumbing / HVAC", "Legal / Accounting", "Retail / Boutique", "Fitness / Gym", "Other local business"].map((t) => (
                          <option key={t} value={t} className="bg-[#0D111A]">{t}</option>
                        ))}
                      </select>
                    </Field>
                    <Field icon={BarChart3} label="Expected call volume" testId="voice-field-volume">
                      <select data-testid="voice-inquiry-input-volume" value={form.call_volume} onChange={set("call_volume")} className={`${inputCls} appearance-none cursor-pointer`}>
                        {VOLUMES.map((v) => (
                          <option key={v} value={v} className="bg-[#0D111A]">{v}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div data-testid="voice-field-functions">
                    <label className="mb-3 block text-xs font-mono font-semibold tracking-widest uppercase text-slate-400">
                      What should your agent do?
                    </label>
                    <div className="flex flex-wrap gap-2.5">
                      {FUNCTIONS.map((fn) => {
                        const on = functions.includes(fn);
                        return (
                          <button
                            key={fn}
                            type="button"
                            data-testid={`voice-inquiry-function-${fn.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => toggleFunction(fn)}
                            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-[border-color,background-color,color] duration-300 ${
                              on
                                ? "border-violet-400/60 bg-violet-500/15 text-violet-200"
                                : "border-white/10 bg-white/5 text-slate-400 hover:border-white/25 hover:text-slate-200"
                            }`}
                          >
                            {fn}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field icon={Clock3} label="Preferred callback time" optional testId="voice-field-time">
                      <input data-testid="voice-inquiry-input-time" type="text" value={form.preferred_call_time} onChange={set("preferred_call_time")} placeholder="Weekdays after 4 PM" className={inputCls} />
                    </Field>
                    <Field icon={NotebookPen} label="Anything else?" optional testId="voice-field-notes">
                      <input data-testid="voice-inquiry-input-notes" type="text" value={form.notes} onChange={set("notes")} placeholder="We get lots of after-hours calls…" className={inputCls} />
                    </Field>
                  </div>

                  <button
                    data-testid="voice-inquiry-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-xl bg-violet-500 px-6 py-4 text-base font-bold text-white hover:bg-violet-400 hover:shadow-[0_0_40px_rgba(112,0,255,0.45)] disabled:opacity-60 transition-[background-color,box-shadow] duration-300"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mic className="h-5 w-5" />}
                    {submitting ? "Sending inquiry…" : "Design my AI voice agent"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2 lg:sticky lg:top-28">
          <SectionHeading
            testId="voice-inquiry-heading"
            kicker="AI voice agent inquiry"
            title="Tell us about your calls. We'll handle the rest."
            sub="Share a few details and we'll design a voice agent trained on your services, pricing and calendar — then hand you a live number to test."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              {[
                { v: "5 min", l: "Setup call" },
                { v: "72h", l: "Agent goes live" },
                { v: "1 ring", l: "Answer speed" },
                { v: "100%", l: "Calls captured" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                  <p className="font-display text-xl font-bold text-violet-300">{s.v}</p>
                  <p className="mt-1 text-xs text-slate-500 font-medium">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
